import React, { useEffect, useState } from "react";
import { Mail, CalendarCheck, MailOpen, Sparkles, TrendingUp } from "lucide-react";
import { adminStats, adminContacts, adminBookings } from "../../lib/api";

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, b] = await Promise.all([adminStats(), adminContacts(), adminBookings()]);
        setStats(s.data);
        setRecentContacts(c.data.slice(0, 5));
        setRecentBookings(b.data.slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tiles = [
    { icon: Mail, label: "Total Contacts", value: stats?.contacts ?? "—" },
    { icon: MailOpen, label: "Unread", value: stats?.unread_contacts ?? "—" },
    { icon: CalendarCheck, label: "Bookings", value: stats?.bookings ?? "—" },
    { icon: TrendingUp, label: "Upcoming", value: stats?.upcoming_bookings ?? "—" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="kicker">Dashboard</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">Overview</h1>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400">
          <Sparkles className="w-4 h-4 text-cyan-300" /> Live metrics
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => {
          const I = t.icon;
          return (
            <div key={t.label} className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500/20 to-cyan-400/20 border border-cyan-400/25 flex items-center justify-center">
                  <I className="w-4 h-4 text-cyan-300" />
                </div>
              </div>
              <div className="mt-4 font-display text-3xl font-bold grad-text">{t.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{t.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Recent Contacts" empty={!loading && recentContacts.length === 0} emptyText="No contact messages yet.">
          {recentContacts.map((c) => (
            <div key={c.id} className="px-5 py-3 border-b border-white/5 last:border-none">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white truncate">{c.name}</div>
                {!c.read && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 uppercase tracking-widest">New</span>}
              </div>
              <div className="text-xs text-slate-400 truncate">{c.email}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.message}</div>
            </div>
          ))}
        </Panel>
        <Panel title="Recent Bookings" empty={!loading && recentBookings.length === 0} emptyText="No consultations booked yet.">
          {recentBookings.map((b) => (
            <div key={b.id} className="px-5 py-3 border-b border-white/5 last:border-none flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">{b.name}</div>
                <div className="text-xs text-slate-400">{b.date} · {b.time}</div>
              </div>
              <StatusPill status={b.status} />
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, children, empty, emptyText }) {
  return (
    <div className="glass rounded-2xl">
      <div className="px-5 py-4 border-b border-white/5">
        <h3 className="font-display text-base font-semibold text-white">{title}</h3>
      </div>
      {empty ? (
        <div className="p-8 text-center text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-200 border-amber-400/30",
    confirmed: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
    cancelled: "bg-red-500/15 text-red-200 border-red-400/30",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${map[status] || map.pending}`}>{status}</span>;
}
